/* What has happened to this contract, read from the tamper-evident audit chain.
 *
 * **The screen used to say this was impossible, and it was right at the time.** Every contract action
 * has always been written to the chain — created, submitted, each signature, activated, retired,
 * evaluated — and no route exposed the entries, so a per-contract timeline could only have been built
 * from what the page happened to see, which would start when you opened it. The chain listing exists
 * now, so the panel reads it. No second table was invented: a store of contract events beside the
 * chain would be a second answer to "what happened here" that could disagree with the one an auditor
 * reads.
 *
 * **It says an evaluation happened and never what it found.** The listing route does not select
 * `payload` — the snapshot never leaves PostgreSQL on this path — so `contract.evaluated` is all
 * there is, and rendering it as "Breached" would be a verdict inferred from the fact that somebody
 * looked. The reference's mock timeline has a Breached row; that row is not available here and the
 * footnote says so rather than leaving a reader to assume the history is quiet because nothing broke.
 *
 * **The approver cannot read this, and the panel is quiet about it rather than broken.** Reading the
 * chain needs `read:audit.chain`; `READER` derives it and `STEWARD` inherits, while `APPROVER`'s
 * grants are hand-curated and do not include it — so the person who signs and retires a contract is
 * exactly the person who cannot read its history. Checking the token before fetching, rather than
 * fetching and styling the 403, matters twice: a refusal we knew was coming does not belong in the
 * tenant's logs, and "you may not see this" must not arrive looking like a failure.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { READ_AUDIT_CHAIN_PERMISSION, can, useIdentity } from '@/entities/session';
import { ACTIVITY_ENTRIES, isSystemActor, useAggregateActivity } from '@/features/audit';
import type { AuditEntry } from '@/shared/api/generated/audit';
import { SkeletonRows } from '@/shared/ui/Skeleton';

/** What the chain files a contract's entries under — `AGGREGATE_TYPE` in `contract_service.py`.
 *
 * The server's own constant, and a value a seven-year chain is keyed on, so it is effectively public
 * API rather than a string this screen picked. */
export const CONTRACT_AGGREGATE = 'DataContract';

/** How each recorded action reads, and how loudly.
 *
 * `signed` and `activated` are the ok tone because they are the promise being made; `retired` is
 * muted because a retired contract is a record rather than a failure; everything else is neutral.
 * **Nothing here is an error tone**, deliberately — no action in this list is a breach, and the one
 * that could be about a breach (`evaluated`) does not say so on this route.
 */
const VERB_TONE: Record<string, 'ok' | 'muted' | 'neutral'> = {
  'contract.created': 'neutral',
  'contract.submitted': 'neutral',
  'contract.signed.producer': 'ok',
  'contract.signed.consumer': 'ok',
  'contract.activated': 'ok',
  'contract.retired': 'muted',
  'contract.evaluated': 'neutral',
};

export interface ContractHistoryProps {
  readonly contractId: string;
  /** The clock, passed in for the reason every relative time in this app is: a component that reads
   * it re-renders with a different answer on every unrelated state change. */
  readonly now: Date;
}

export function ContractHistory({ contractId, now }: ContractHistoryProps): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();

  if (!can(identity.data, READ_AUDIT_CHAIN_PERMISSION)) {
    return (
      <section className="lq-ct__panel lq-ct__history">
        <h2 className="lq-ct__panelTitle">{t('contracts.history.section')}</h2>
        <span className="lq-ct__restricted">
          <span className="lq-ct__restrictedDot" aria-hidden="true" />
          {t('contracts.history.restricted')}
        </span>
        <p className="lq-note">{t('contracts.history.restrictedWhy')}</p>
      </section>
    );
  }

  return <Entries contractId={contractId} now={now} />;
}

function Entries({ contractId, now }: ContractHistoryProps): ReactElement {
  const { t } = useTranslation();
  const activity = useAggregateActivity(CONTRACT_AGGREGATE, contractId);
  const entries = activity.data?.items ?? [];

  return (
    <section className="lq-ct__panel lq-ct__history">
      <h2 className="lq-ct__panelTitle">{t('contracts.history.section')}</h2>

      {activity.isPending && <SkeletonRows rows={3} columns={3} />}

      {/* A failed read is a failed read, never an empty history. "Nothing has happened to this
          contract" is a claim about the record, and using it to report our own failed request is the
          class this codebase has now recorded at four screens. */}
      {activity.isError && <p className="lq-note">{t('contracts.history.unread')}</p>}

      {activity.isSuccess && entries.length === 0 && (
        <p className="lq-note">{t('contracts.history.none')}</p>
      )}

      {activity.isSuccess && entries.length > 0 && (
        <>
          <ul className="lq-ct__timeline">
            {entries.map((entry) => (
              <li
                key={`${entry.chain_date}#${String(entry.sequence)}`}
                className="lq-ct__event"
              >
                {/* Formatted here rather than through ICU: `i18next-icu` 2.4.4 cannot render ICU's
                    `time` type — a message using it comes back as its own source, braces and all. */}
                <span className="lq-ct__eventWhen lumiq-numeric">
                  {whenOf(entry, now)}
                </span>
                <span
                  className={`lq-ct__eventVerb lq-ct__eventVerb--${VERB_TONE[entry.action] ?? 'neutral'}`}
                >
                  {t(`contracts.history.action.${entry.action}`, {
                    defaultValue: t('contracts.history.action.unknown', {
                      action: entry.action,
                    }),
                  })}
                </span>
                {/* A `system:` actor is an unattended sweep rather than a person — the hourly
                    evaluation writes one — and the two must not look alike: "who did this" has a
                    different answer and a different follow-up for each. */}
                <span
                  className={
                    isSystemActor(entry.actor)
                      ? 'lq-ct__eventWho lq-ct__eventWho--system'
                      : 'lq-ct__eventWho'
                  }
                >
                  {entry.actor}
                </span>
              </li>
            ))}
          </ul>
          {entries.length === ACTIVITY_ENTRIES && (
            /* Truncation says so with the exact count. There is no pager here, so a full list
               claiming to be the whole history would be the `next_cursor: null`-while-truncating
               defect in a card. */
            <p className="lq-note">
              {t('contracts.history.capped', { count: ACTIVITY_ENTRIES })}
            </p>
          )}
          <p className="lq-note">{t('contracts.history.noSnapshot')}</p>
        </>
      )}
    </section>
  );
}

/* A clock time for today's entries and a real date for older ones.
 *
 * A history read top to bottom wants "this morning" without arithmetic, and wants a date once an
 * entry is old enough that a relative age stops being useful — "38 days ago" is a worse answer than
 * "19 Jul 2026" for something a reader may be about to cite in an audit. The reader's locale and the
 * reader's own midnight, which is the call every other activity panel here already makes.
 */
function whenOf(entry: AuditEntry, now: Date): string {
  const at = new Date(entry.occurred_at);
  if (Number.isNaN(at.getTime())) return entry.chain_date;
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  return sameDay
    ? at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}
